import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { pingDealerActivity, recordDealerLogout } from '../api/api';

const SESSION_KEY = 'dealer_activity_session_id';

export function useActivityTracker(isAuthenticated: boolean) {
    const location = useLocation();
    
    // Store current page info in refs so setInterval closure can access latest values
    const currentUrl = useRef(location.pathname);
    const timeSpentOnPage = useRef(0);
    const geoData = useRef<{ latitude?: number; longitude?: number }>({});
    
    // Session ID management
    const getSessionId = () => {
        let sid = sessionStorage.getItem(SESSION_KEY);
        if (!sid) {
            // Use localStorage if saved by authSlice
            sid = localStorage.getItem("dealer_activity_session_id");
            if (sid) sessionStorage.setItem(SESSION_KEY, sid);
        }
        return sid;
    };

    // 1. Fetch Geolocation Once
    useEffect(() => {
        if (!isAuthenticated) return;
        
        if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    geoData.current = {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude
                    };
                },
                (error) => {
                    console.log('ActivityTracker: Geolocation permission denied or error', error);
                },
                { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
            );
        }
    }, [isAuthenticated]);

    // 2. Route Change Detection
    useEffect(() => {
        if (!isAuthenticated) return;

        // When route changes, reset the page timer and update the current URL reference
        currentUrl.current = location.pathname;
        timeSpentOnPage.current = 0;
        
        // Fire an immediate ping for the new page
        sendPing();
    }, [location.pathname, isAuthenticated]);

    // 3. Heartbeat (Every 30 seconds)
    useEffect(() => {
        if (!isAuthenticated) return;

        const interval = setInterval(() => {
            timeSpentOnPage.current += 30; // increment by interval duration
            sendPing();
        }, 30000);

        return () => clearInterval(interval);
    }, [isAuthenticated]);

    const sendPing = () => {
        const sid = getSessionId();
        if (!sid) return;
        pingDealerActivity({
            sessionId: sid,
            url: currentUrl.current,
            pageTitle: document.title || 'Dealer Portal',
            timeSpentSeconds: timeSpentOnPage.current,
            latitude: geoData.current.latitude,
            longitude: geoData.current.longitude
        }).catch(err => {
            console.error('Activity ping failed:', err);
        });
    };
    
    // We expose a manual logout trigger so the logout button can call it
    const triggerLogoutActivity = async (reason: 'MANUAL_LOGOUT' | 'TOKEN_EXPIRED' | 'INACTIVITY_TIMEOUT' = 'MANUAL_LOGOUT') => {
        try {
            const sid = getSessionId();
            if (!sid) return;
            await recordDealerLogout({
                sessionId: sid,
                reason,
                latitude: geoData.current.latitude,
                longitude: geoData.current.longitude
            });
        } catch (e) {
            console.error('Failed to record logout', e);
        }
        sessionStorage.removeItem(SESSION_KEY);
    };

    return { triggerLogoutActivity };
}
